import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getNationalIntelligence } from '../../api/national.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import QuarterlyReportButton from '../../components/QuarterlyReportButton.jsx'

import Overview from './tabs/Overview.jsx'
import DiseaseBurden from './tabs/DiseaseBurden.jsx'
import ChronicConditions from './tabs/ChronicConditions.jsx'
import RiskFactors from './tabs/RiskFactors.jsx'
import WorkforceHealth from './tabs/WorkforceHealth.jsx'
import ScreeningAnalytics from './tabs/ScreeningAnalytics.jsx'
import GeographicInsights from './tabs/GeographicInsights.jsx'
import TrendsForecasts from './tabs/TrendsForecasts.jsx'
import ValueImpact from './tabs/ValueImpact.jsx'

/**
 * National Health Intelligence — the central population-health analytics
 * platform for ICER Health. Platform-admin only.
 *
 * One integrated, tabbed experience (NOT a pile of standalone reports).
 * Every eligible health record — from any organisation, individual
 * subscriber, or member of the general public — contributes to these
 * national views via the /national/intelligence/ aggregator.
 *
 * EXTENSIBILITY: the whole experience is driven by the TABS registry
 * below. Each entry is `{ id, label, Component, selfFetch? }`. To add a
 * future intelligence module (TB surveillance, maternal/child health,
 * immunisation, outbreak monitoring, ...) you:
 *   1. add a section to the backend aggregator,
 *   2. drop a new tab component in ./tabs/,
 *   3. register it here.
 * No other file changes, no redesign. Section components receive the
 * shared `{ data, windowDays }` payload; tabs that manage their own data
 * (e.g. Value & Impact) set `selfFetch: true` and the shell skips the
 * shared fetch chrome for them.
 */
const TABS = [
  { id: 'overview', label: 'Overview', Component: Overview },
  { id: 'disease-burden', label: 'Disease Burden', Component: DiseaseBurden },
  { id: 'chronic', label: 'Chronic Conditions', Component: ChronicConditions },
  { id: 'risk', label: 'Risk Factors', Component: RiskFactors },
  { id: 'workforce', label: 'Workforce Health', Component: WorkforceHealth },
  { id: 'screening', label: 'Screening Analytics', Component: ScreeningAnalytics },
  { id: 'geographic', label: 'Geographic Insights', Component: GeographicInsights },
  { id: 'trends', label: 'Trends & Forecasts', Component: TrendsForecasts },
  { id: 'value', label: 'Value & Impact', Component: ValueImpact, selfFetch: true },
]

export default function NationalHealthIntelligence() {
  const [tabId, setTabId] = useState('overview')
  const [days, setDays] = useState(30)

  const active = TABS.find((t) => t.id === tabId) || TABS[0]
  const sharedFetch = !active.selfFetch

  const q = useQuery({
    queryKey: ['national-intelligence', days],
    queryFn: () => getNationalIntelligence({ days }),
    // Self-fetching tabs (Value & Impact) don't need the shared payload;
    // the query still warms the cache for when the admin tabs back.
    enabled: true,
    staleTime: 60_000,
  })

  return (
    <div className="space-y-4">
      {/* ---- Header + window control ---- */}
      <Card>
        <CardHeader
          title="National Health Intelligence"
          subtitle="Cumulative population-health analytics across every participating organisation, employer, clinic, individual subscriber and member of the general public."
          right={
            sharedFetch ? (
              <div className="flex items-center gap-2">
                <label className="text-xs text-slate-500">Window:</label>
                <select
                  value={days}
                  onChange={(e) => setDays(Number(e.target.value))}
                  className="border rounded px-2 py-1 text-xs bg-white"
                >
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                  <option value={180}>Last 180 days</option>
                  <option value={365}>Last 365 days</option>
                </select>
              </div>
            ) : (
              <span className="text-[10px] uppercase tracking-wide border rounded-full px-2 py-0.5 bg-slate-100 text-slate-700 border-slate-300">
                ICER internal
              </span>
            )
          }
        />

        {/* ---- Tab strip ---- */}
        <div className="border-b-2 border-slate-200 flex flex-wrap gap-1 -mb-px">
          {TABS.map((t) => {
            const isActive = t.id === tabId
            return (
              <button
                key={t.id}
                onClick={() => setTabId(t.id)}
                className={
                  'px-4 py-2.5 text-sm tracking-wide border-b-[3px] -mb-[2px] transition-colors whitespace-nowrap ' +
                  (isActive
                    ? 'border-wantok-700 text-wantok-800 font-bold bg-wantok-50/60'
                    : 'border-transparent text-slate-600 font-semibold hover:text-wantok-800 hover:bg-slate-50')
                }
              >
                {t.label}
              </button>
            )
          })}
        </div>
      </Card>

      <QuarterlyReportButton scope="national" />

      {/* ---- Active tab ---- */}
      {active.selfFetch ? (
        <active.Component />
      ) : (
        <>
          {q.error && <ErrorBanner error={q.error} onRetry={() => q.refetch()} />}
          {q.isLoading && (
            <Card>
              <div className="text-sm text-slate-400">Loading national intelligence…</div>
            </Card>
          )}
          {q.data && <active.Component data={q.data} windowDays={q.data.window_days || days} />}

          {q.data && (
            <div className="text-[11px] text-slate-500 italic">
              Computed {q.data.computed_at ? new Date(q.data.computed_at).toLocaleString() : ''} ·
              Aggregate-only · Platform-admin view · k={q.data.privacy_floor_k} privacy floor on
              every breakdown bucket · Every participating organisation and individual contributes.
            </div>
          )}
        </>
      )}
    </div>
  )
}
